import { Component, inject, Input, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as saveAs from "file-saver";
import { AttachmentModel } from 'app/models/attachment.model';
import { FormService } from 'app/services/form.service';
import { UserService } from 'app/users/user.service';
import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DocumentConstants } from 'app/constants/document.constants';
import { FormValidator } from '../form/form-validators/form-validators';
import { fieldPatterns } from 'app/utilities/form.utilities';

@Component({
  selector: 'app-uploaded-file',
  templateUrl: './uploaded-file.component.html',
  styleUrls: ['./uploaded-file.component.scss']
})
export class UploadedFileComponent implements OnInit {
  @Input({required: true}) attachment!: AttachmentModel;
  @Input({required: true}) requiresAdmin!: boolean;
  private _fileExtension!: string;
  public isAmaAdmin!: boolean;
  private _snackBar = inject(MatSnackBar);
  
  // For readability in the validators
  private FORM_A = 'FormA';
  private FORM_B = 'FormB';
  private FORM_C = 'FormC';
  private FORM_D = 'FormD';
  private FORM_E = 'FormE';
  private FORM_F = 'FormF';

  private MOLECULAR_FORMULA_VALIDATORS: ValidatorFn[] = [ FormValidator.isRequiredForFormType(this.formService.getCurrentUsanFormType(), [this.FORM_B, this.FORM_C, this.FORM_D, this.FORM_F]), Validators.pattern(fieldPatterns.molecularFormula), FormValidator.validParenthesis()];
  private MOLECULAR_FORMULA_SINGLE_ENTITY_VALIDATORS: ValidatorFn[] = [ FormValidator.isRequiredForFormType(this.formService.getCurrentUsanFormType(), [this.FORM_A]), Validators.pattern(fieldPatterns.molecularFormula), FormValidator.validParenthesis()];
  private MOLECULAR_FORMULA_FOR_SALT_VALIDATORS: ValidatorFn[] = [ FormValidator.isRequiredForFormType(this.formService.getCurrentUsanFormType(), [this.FORM_A]), Validators.pattern(fieldPatterns.molecularFormula), FormValidator.validParenthesis()];
  
  private VALIDATOR_ADDITIONS_ON_REMOVE_MAP: Map<string, ValidatorFn[]> = new Map([
    [DocumentConstants.MOLECULAR_FORMULA, this.MOLECULAR_FORMULA_VALIDATORS],
    [DocumentConstants.MOLECULAR_FORMULA_SINGLE_ENTITY, this.MOLECULAR_FORMULA_SINGLE_ENTITY_VALIDATORS],
    [DocumentConstants.MOLECULAR_FORMULA_FOR_SALT, this.MOLECULAR_FORMULA_FOR_SALT_VALIDATORS]
  ]);

  private FILE_NAME_TO_FILED_NAME_MAP: Map<string, string> = new Map([
    [DocumentConstants.MOLECULAR_FORMULA,'molecularFormula' ],
    [DocumentConstants.MOLECULAR_FORMULA_SINGLE_ENTITY, 'molecularFormulaForSingleEntity'],
    [DocumentConstants.MOLECULAR_FORMULA_FOR_SALT, 'molecularFormulaForSaltForm']
  ]);

  constructor(private userService: UserService, private formService: FormService) {}

  ngOnInit(): void {
    this.isAmaAdmin = this.userService.profile.userRole === 'USAN AMA Admin';
    this.extractFileExtension();
  }

  get canDisplayMenu(): boolean {
    return this.requiresAdmin ? this.isAmaAdmin : true;
  }
  
  private extractFileExtension() {
    const extnIdx = this.fileName.lastIndexOf('.') + 1; // So we skip the dot.
    this._fileExtension = this.fileName.slice(extnIdx);
  }

  private fileNameNoExtension() {
    const extnIdx = this.fileName.lastIndexOf('.');
    return this.fileName.slice(0, extnIdx);
  }

  get fileName(): string {
    return this.attachment.file_name;
  }

  get fileType(): string {
    return this.fileTypeByExtension();
  }

  get fileExtension(): string {
    return this._fileExtension;
  }

  get iconPath(): string {
    const filePath = `assets/icons/`;
    switch (this.fileTypeByExtension()) {
      // For ChemDraw files.
      case `ChemDraw File`:
        return filePath + `chem-draw-icon.png`;
      // For CSV files
      case `CSV File`:
        return filePath + `csv-icon.png`;
      // For Word documents
      case `MS Word Document`:
        return filePath + `word-doc-icon.png`;
      // For Excel documents
      case `MS Excel Spreadsheet`:
        return filePath + `excel-icon.png`;
      // For images
      case `Image File`:
        return filePath + `image-icon.png`;
      // For PDFs
      case `PDF`:
        return filePath + `pdf-icon.png`;
      default:
        return filePath + `file-icon.png`;
    }
  }

  private fileTypeByExtension() {
    switch (this.fileExtension) {
      // For ChemDraw files.
      case `cdx`:
      case `cdxml`:
        return `ChemDraw File`;
      // For CSV files
      case `csv`:
        return `CSV File`;
      // For Word documents
      case `doc`:
      case `docx`:
        return `MS Word Document`;
      // For Excel documents
      case `xls`:
      case `xlsx`:
        return `MS Excel Spreadsheet`;
      // For images
      case `bmp`:
      case `gif`:
      case `jpeg`:
      case `jpg`:
      case `png`:
      case `svg`:
      case `tif`:
      case `tiff`:
        return `Image File`;
      // For PDFs
      case `pdf`:
        return `PDF`;
      default:
        return this.fileExtension?.toUpperCase() + ` File`;
    }
  }

  public removeAttachment() {
    this.formService.removeAttachment(this.attachment)
    .subscribe({
      next: () => {
        this.updateAttachments();
      },
      error: (err) => {
        console.error(err);
        this._snackBar.open("There was an error while attempting to remove the attachment. Please try again.", "Close", { duration: 3000 });
        return err;
      }
    });
  }

  updateValidatorsForAttachmentType() {
    if(this.FILE_NAME_TO_FILED_NAME_MAP.has(this.fileNameNoExtension())) {
      this.addValidators();
    }
  }
  
  public addValidators() {
    let formFieldName = this.FILE_NAME_TO_FILED_NAME_MAP.get(this.fileNameNoExtension());
    if(!formFieldName) {
      return;
    }

    const targetControl = this.currentForm.get(formFieldName);

    if (!targetControl) {
        return;
    }
    
    const formFieldValidators = this.VALIDATOR_ADDITIONS_ON_REMOVE_MAP.get(this.fileNameNoExtension());
    if(!formFieldValidators) {
      return;
    }

    targetControl.setValidators(formFieldValidators);

    // Force re-validation if not the initial call
    setTimeout(() => {
      targetControl.markAsTouched();
      targetControl.markAsDirty();
      targetControl.updateValueAndValidity();
      this.formService.invalidFormFields();
    });
  }

  public downloadAttachment() {
    this.formService.downloadAttachment(this.attachment.sys_id).subscribe({
      next: res => {
        if(res) {
            saveAs(res, this.attachment.file_name);
        }
      },
      error: error => {
        this._snackBar.open(`Error occurred while downloading attachment.`, `Close`, { duration: 3000 });
      }  
    }); 
  }

  private updateAttachments() {
    const filename = this.attachment.file_name;
    this.formService.callFetchAttachmentsForCase().subscribe({
      next: attachments => {
        if(!attachments || !attachments.hasAttachmentsForSection(this.fileNameNoExtension())) {
          this.updateValidatorsForAttachmentType();
        }

        this._snackBar.open(`${filename} was removed.`, "Close", { duration: 3000 });
      },
      error: (err) => {
        console.error(err);
        this._snackBar.open("There was an error while trying to update the attachments.", "Close", { duration: 3000 });
        return err;
      }
    });
  }

  get currentForm(): FormGroup {
    return this.formService.getCurrentUsanFormValue();
  }
}
